"use client";

import { useEffect, useRef } from "react";
import { useSWRConfig } from "swr";
import { useOptionalRefresh } from "@/app/context/RefreshContext";

export type DataRefreshPolicy = "live" | "daily" | "snapshot";
// Metadata only. Response objects remain exclusively in the shared SWR cache.
const epochs = new Map<string, { job: number | null; observed: number }>();
const MAX_REFRESH_KEYS = 512;
// One invalidation per completed job and SWR owner. Calling bound mutate from
// every consumer bypasses SWR's mount/focus deduplication and repeats the GET.
// Separate providers must still refresh their independent response caches.
const liveJobs = new WeakMap<object, Map<string, number>>();
const snapshots = new WeakMap<object, Map<string, { job: number | null; observed: number }>>();
function claimLiveRefresh(cache: object, key: string, job: number): boolean {
  let jobs = liveJobs.get(cache);
  if (!jobs) { jobs = new Map(); liveJobs.set(cache, jobs); }
  if (jobs.get(key) === job) return false;
  jobs.delete(key); jobs.set(key, job);
  while (jobs.size > MAX_REFRESH_KEYS) jobs.delete(jobs.keys().next().value!);
  return true;
}

/** Live tables validate on visibility/focus through SWR. Daily snapshots do
 * not poll: remember completed jobs per exact identity/URL so a retained or
 * remounted screen catches a refresh it missed while its effects were paused.
 * An initial status response describing an old job never triggers a refresh. */
export function useDataRefresh(key: string | null, mutate: () => Promise<unknown>, policy: DataRefreshPolicy) {
  const { cache } = useSWRConfig();
  const refresh = useOptionalRefresh(), job = refresh?.lastJobAt ?? null, status = refresh?.status;
  const seen = useRef({ key, job, running: status === "running" });
  useEffect(() => {
    if (policy === "snapshot") {
      if (!key) return;
      let observed = snapshots.get(cache);
      if (!observed) { observed = new Map(); snapshots.set(cache, observed); }
      const before = observed.get(key);
      observed.delete(key); observed.set(key, { job, observed: Date.now() });
      while (observed.size > MAX_REFRESH_KEYS) observed.delete(observed.keys().next().value!);
      // A retained/unmounted ad view may miss a completed job while still
      // owning cached data (legacy page dedupes for 2h). Catch that completion
      // on return, but don't refetch just because initial status has an old job.
      if (job != null && before && before.job !== job && (before.job != null || job >= before.observed) && claimLiveRefresh(cache, key, job)) void mutate();
      return;
    }
    if (policy === "daily") {
      if (!key) return;
      const before = epochs.get(key);
      epochs.delete(key); epochs.set(key, { job, observed: Date.now() });
      while (epochs.size > MAX_REFRESH_KEYS) epochs.delete(epochs.keys().next().value!);
      if (job != null && before && before.job !== job && (before.job != null || job >= before.observed)) void mutate();
      return;
    }
    if (seen.current.key !== key) { seen.current = { key, job, running: status === "running" }; return; }
    if (status === "running") seen.current.running = true;
    if (!job || job === seen.current.job) return;
    const changed = seen.current.job !== null || seen.current.running;
    seen.current = { key, job, running: false };
    if (changed && key && document.visibilityState !== "hidden" && claimLiveRefresh(cache, key, job)) void mutate();
  }, [cache, key, job, status, mutate, policy]);
}
